import { SelectionModel } from '@angular/cdk/collections';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDragPreview,
  CdkDragSortEvent,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  TrackByFunction,
  computed,
  effect,
  inject,
  input,
  output,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatMiniFabButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOption, MatRipple } from '@angular/material/core';
import { MatFormField, MatLabel, MatPrefix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatNoDataRow,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { ITableState, TableStateService } from '@core/services/table-state.service';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SkeletonLoaderComponent } from '@shared/components/skeleton-loader/skeleton-loader.component';
import { ISkeletonLoaderConfig } from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { get } from 'lodash';
import { BehaviorSubject, Observable, Subscription, combineLatest, debounceTime, merge, startWith } from 'rxjs';
import { map, skip } from 'rxjs/operators';
import {
  EHeaderClasses,
  ETableCrudScope,
  ICustomColumn,
  IDataRefreshRequestParams,
  IDatatableColumn,
  IDatatableConfiguration,
  IRowClickEvent,
  TConfigurationDefaults,
  TCustomColumn,
  TDatatableColumn,
  TFormattedDatatableColumn,
} from './datatable.interface';

@Component({
  imports: [
    CdkDropList,
    CdkDrag,
    MatFormField,
    MatMiniFabButton,
    MatHeaderCell,
    MatPaginator,
    MatCellDef,
    MatCell,
    MatButton,
    MatHeaderRowDef,
    MatSortHeader,
    MatColumnDef,
    MatSort,
    MatTable,
    ReactiveFormsModule,
    NgTemplateOutlet,
    MatRowDef,
    CdkDragPreview,
    MatCheckbox,
    MatHeaderRow,
    MatRipple,
    CdkDragHandle,
    MatNoDataRow,
    MatHeaderCellDef,
    MatInput,
    MatSelect,
    MatOption,
    MatRow,
    NgClass,
    TranslateModule,
    MatPrefix,
    SkeletonLoaderComponent,
    IconComponent,
    MatLabel,
  ],
  selector: 'app-datatable',
  styleUrl: './datatable.component.scss',
  templateUrl: './datatable.component.html',
})
export class DatatableComponent<RowData extends { id: idType }, idType extends number | string = number>
  implements OnDestroy, OnInit, AfterViewInit
{
  public readonly total = input.required<number>();
  public readonly rowData = input.required<RowData[]>();
  public readonly columns = input.required<TDatatableColumn<RowData, idType>[]>();
  public readonly configurationInput = input.required<IDatatableConfiguration<RowData>>();

  public readonly isInitialDataLoaded = input<boolean>(false);
  public readonly loadingData = input<boolean>(false);
  public readonly loaderConfig = input<ISkeletonLoaderConfig | null>(null);
  public readonly customColumnDef = input<ICustomColumn[]>([]);
  // formattedRowData rebuilds every row object on each rowData change, so with the default (row
  // identity, same as mat-table without a trackBy) every row is destroyed and re-created - images
  // in cells reload. Tables that refresh rows in place should pass a key-based trackBy instead.
  public readonly trackBy = input<TrackByFunction<RowData>>((_index, row) => row);

  public readonly rowClick = output<IRowClickEvent<RowData, idType>>();
  public readonly rowOrderChange = output<idType[]>();
  public readonly addClick = output<void>();
  public readonly editClick = output<void>();
  public readonly deleteClick = output<void>();

  @Output() public readonly dataRefreshRequest = new EventEmitter<IDataRefreshRequestParams>();
  @Output() public readonly selectedItemsChange = new EventEmitter<idType[]>();

  public readonly table = viewChild.required<MatTable<RowData>>('table');
  public readonly paginatorElement = viewChild(MatPaginator);
  public readonly sortElement = viewChild(MatSort);

  public readonly dataSource: MatTableDataSource<RowData> = new MatTableDataSource<RowData>();

  public readonly defaultConfigurations: TConfigurationDefaults<IDatatableConfiguration<RowData>> = {
    addButtonText: null,
    addScope: ETableCrudScope.none,
    areCheckboxesEnabled: false,
    areCheckboxesSelectedByDefault: false,
    dataRefreshEvent: null,
    defaultSort: { active: 'created_at', direction: 'desc' },
    deleteScope: ETableCrudScope.none,
    disableCheckboxCondition: () => false,
    downloadScope: { excelTemplate: false, excelWithData: false },
    editScope: ETableCrudScope.none,
    externalSearchTerm: null,
    filters: [],
    isClientSide: true,
    isDraggable: false,
    isHeaderRowEnabled: true,
    isLoaderEnabled: true,
    isPaginationEnabled: true,
    isRowClickEnabled: true,
    isSearchEnabled: true,
    pageSizeOptions: [5, 10, 25, 50, 100],
    rowColor: null,
    showNoDataText: true,
    skipInitialRequest: true,
    specialRows: [],
    storageKey: null,
    tableMargin: '0',
    tablePadding: '0',
  };
  public readonly configurations: Signal<Required<IDatatableConfiguration<RowData>>> = computed(
    (): Required<IDatatableConfiguration<RowData>> => {
      const configurations = {
        ...this.defaultConfigurations,
        ...this.configurationInput(),
      };

      configurations.specialRows = configurations.specialRows.map((specialRow) => ({
        deleteCondition: specialRow.deleteCondition.bind(this),
        editCondition: specialRow.editCondition.bind(this),
        requirement: specialRow.requirement.bind(this),
      }));

      return configurations;
    },
  );

  public readonly sort = new BehaviorSubject<Sort>(this.defaultConfigurations.defaultSort);
  public readonly search = new FormControl();
  public readonly activeFilters = new BehaviorSubject<Record<string, unknown>>({});
  public readonly paginator = new BehaviorSubject<{ pageIndex: number; pageSize: number }>({
    pageIndex: 0,
    pageSize: 5,
  });
  public readonly paginatorState = toSignal(this.paginator, { initialValue: this.paginator.value });

  public readonly columnNames: Signal<string[]> = computed(() => {
    const beforeInputColumns = this.configurations().areCheckboxesEnabled
      ? ['checkbox']
      : this.configurations().isDraggable
        ? ['drag']
        : [];
    const afterColumns =
      this.configurations().areCheckboxesEnabled && this.configurations().isDraggable ? ['drag'] : [];

    return [...beforeInputColumns, ...this.columnNamesWithoutStatic(), ...afterColumns];
  });

  public readonly simpleColumns: Signal<TFormattedDatatableColumn<RowData, idType>[]> = computed(() => {
    const customColumnNames = this.customColumnDef().map((column) => column.name);

    return this.columns()
      .filter((column) => column.id !== 'id')
      .map((column, index) => ({
        ...column,
        headerClasses: column.headerClasses?.join(' ') || `${EHeaderClasses.maxWidth300} ${EHeaderClasses.minWidth50}`,
        uniqueColumnId: this.columnNamesWithoutStatic()[index],
      }))
      .filter((column) => !customColumnNames.includes(column.id));
  });
  public readonly customColumns: Signal<TCustomColumn[]> = computed(() => {
    return this.customColumnDef().map((customColumn) => {
      const index = this.columns()
        .filter((column) => column.id !== 'id')
        .findIndex((column) => column.id === customColumn.name);

      return {
        ...customColumn,
        headerClasses:
          customColumn.headerClasses?.join(' ') || `${EHeaderClasses.maxWidth300} ${EHeaderClasses.minWidth50}`,
        uniqueColumnId: index === -1 ? customColumn.name : this.columnNamesWithoutStatic()[index],
      };
    });
  });

  public readonly formattedRowData: Signal<RowData[]> = computed(() =>
    this.rowData().map((rowData) =>
      this.columns().reduce(
        (reducePayload, column, index) => ({
          ...reducePayload,
          [this.columnNamesWithoutStatic()[index]]: column.dataViewStrategy
            ? column.dataViewStrategy(rowData)
            : get(rowData, column.id),
        }),
        rowData,
      ),
    ),
  );

  public readonly checkboxModel: SelectionModel<idType> = new SelectionModel<idType>(true, []);
  public readonly ECrudScope = ETableCrudScope;

  public readonly selectedItems = toSignal(this.selectedItemsChange.pipe(startWith([])));
  public readonly maxEditScope = computed(() => {
    this.selectedItems();

    return this.configurations().specialRows.reduce((reducePayload, specialRow) => {
      if (reducePayload === ETableCrudScope.none || !specialRow.requirement()) {
        return reducePayload;
      }

      return Math.min(reducePayload, specialRow.editCondition());
    }, ETableCrudScope.bulk);
  });
  public readonly maxDeleteScope = computed(() => {
    this.selectedItems();

    return this.configurations().specialRows.reduce((reducePayload, specialRow) => {
      if (reducePayload === ETableCrudScope.none || !specialRow.requirement()) {
        return reducePayload;
      }

      return Math.min(reducePayload, specialRow.deleteCondition());
    }, ETableCrudScope.bulk);
  });

  public readonly isCheckedStatuses = computed(() => {
    return this.rowData().map((row) => ({ isChecked: this.checkboxValues().includes(row.id) }));
  });
  public readonly isAllSelected = computed(
    () =>
      Boolean(this.checkboxValues().length) &&
      this.checkboxValues().length ===
        this.rowData().filter((row: RowData) => !this.configurations().disableCheckboxCondition(row)).length,
  );

  public currentDragIndex: number | null = null;
  public isLoading = true;

  private readonly tableStateService = inject(TableStateService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  private readonly checkboxValues = toSignal(this.checkboxModel.changed.pipe(map(() => this.checkboxModel.selected)), {
    initialValue: [],
  });
  private readonly columnNamesWithoutStatic = computed(() =>
    this.columns()
      .filter((column) => column.id !== 'id')
      .map((column, index) => `${column.id}||${index}`),
  );
  private readonly subscriptions: Subscription[] = [];

  private isFirstRowDataChange = true;
  private initialLoaderDisplayed = false;
  /** Tracks whether the one-time initial `skipInitialRequest` skip has already happened. Kept as
   * its own field (rather than mutating `configurations().skipInitialRequest` in place) because
   * `configurations()` is a memoized computed signal - it rebuilds a brand-new merged object
   * whenever `configurationInput()` changes reference (e.g. a caller rebuilding its config object
   * once async filter options load), which would otherwise silently discard the mutation and
   * reset the flag to `true`, swallowing the next real data refresh request. */
  private hasSkippedInitialRequest = false;

  constructor() {
    effect(() => {
      const configurations = untracked(() => this.configurations());

      if (this.isFirstRowDataChange) {
        this.isFirstRowDataChange = false;
      } else {
        this.isLoading = false;
      }

      this.dataSource.data = this.formattedRowData();

      if (configurations.areCheckboxesEnabled && configurations.areCheckboxesSelectedByDefault) {
        this.checkboxModel.setSelection(...this.rowData().map((row) => row.id));
      } else {
        this.checkboxModel.clear();
      }
    });

    effect(() => {
      const { defaultSort } = this.configurations();
      const baseDefaultSort = this.defaultConfigurations.defaultSort;
      const currentSort = this.sort.value;
      const isCurrentBaseDefault: boolean =
        currentSort.active === baseDefaultSort.active && currentSort.direction === baseDefaultSort.direction;
      const hasDifferentDefault: boolean =
        defaultSort.active !== baseDefaultSort.active || defaultSort.direction !== baseDefaultSort.direction;

      if (isCurrentBaseDefault && hasDifferentDefault) {
        this.sort.next(defaultSort);
      }
    });
  }

  public ngOnInit(): void {
    this.restoreTableStateFromStorage();

    this.subscriptions.push(
      combineLatest({
        page: this.paginator,
        sort: this.sort,
      }).subscribe(({ page, sort }) => {
        if (!this.initialLoaderDisplayed) {
          this.initialLoaderDisplayed = this.configurations().isLoaderEnabled && this.configurations().isClientSide;
          this.isLoading = true;
        }

        if (this.configurations().skipInitialRequest && !this.hasSkippedInitialRequest) {
          this.hasSkippedInitialRequest = true;

          this.persistTableState(page, sort);

          return;
        }

        const splitSortTarget = sort.active.split('||');
        const active =
          splitSortTarget.length > 1 ? splitSortTarget.slice(0, splitSortTarget.length - 1).join('||') : sort.active;

        this.dataRefreshRequest.emit({
          filters: this.activeFilters.value,
          limit: page.pageSize,
          page: page.pageIndex + 1,
          sort: sort.direction ? { ...sort, active } : this.configurations().defaultSort,
          ...(this.configurations().isSearchEnabled ? { search: this.search.value } : {}),
        });

        this.persistTableState(page, sort);
      }),
      this.checkboxModel.changed.subscribe(() => {
        this.selectedItemsChange.emit(this.checkboxModel.selected);
      }),
      merge(
        this.configurations().dataRefreshEvent ?? new Observable<unknown>(),
        this.search.valueChanges.pipe(debounceTime(600)),
        this.activeFilters.pipe(skip(1), debounceTime(100)),
      ).subscribe(() => {
        this.paginator.next({ ...this.paginator.value, pageIndex: 0 });
      }),

      ...(this.configurations().isClientSide
        ? [
            this.search.valueChanges.subscribe((search: string) => {
              this.dataSource.filter = (search ?? '').trim().toLowerCase();
            }),
          ]
        : []),
    );

    this.prepareFilterPredicate();
  }

  public ngAfterViewInit(): void {
    if (!this.configurations().isClientSide) {
      return;
    }

    const paginator: MatPaginator | undefined = this.paginatorElement();
    const sort: MatSort | undefined = this.sortElement();

    if (paginator) {
      this.dataSource.paginator = paginator;
    }

    if (sort) {
      this.dataSource.sort = sort;
      this.dataSource.sortingDataAccessor = (row: RowData, active: string): string | number => {
        const splitSortTarget = active.split('||');
        const activeId =
          splitSortTarget.length > 1 ? splitSortTarget.slice(0, splitSortTarget.length - 1).join('||') : active;

        const column = this.columns()
          .filter((c) => c.id !== 'id')
          .find((c) => c.id === activeId);

        if (!column) {
          return (row as unknown as Record<string, string | number>)[activeId] ?? '';
        }

        if (column.dataViewStrategy) {
          const value = column.dataViewStrategy(row);

          return value as string | number;
        }

        return (row as unknown as Record<string, string | number>)[activeId] ?? '';
      };
    }
  }

  public onFilterChange(filterKey: string, value: unknown): void {
    const nextFilters: Record<string, unknown> = { ...this.activeFilters.value, [filterKey]: value };

    this.activeFilters.next(nextFilters);
  }

  public toggleAllRows(): void {
    if (this.isAllSelected()) {
      this.checkboxModel.clear();

      return;
    }

    this.checkboxModel.select(
      ...this.rowData()
        .filter((row: RowData) => !this.configurations().disableCheckboxCondition(row))
        .map((row) => row.id),
    );
  }

  public drop($event: CdkDragDrop<string>): void {
    const previousIndex: number = this.rowData().findIndex((rowData: RowData) => rowData === $event.item.data);

    moveItemInArray(this.rowData(), previousIndex, $event.currentIndex);
    this.dataSource.data = this.rowData();
    this.rowOrderChange.emit(this.rowData().map((rowData: RowData) => rowData.id));
    this.table().renderRows();
  }

  public dragSorted($event: CdkDragSortEvent): void {
    this.currentDragIndex = $event.currentIndex;
    this.changeDetectorRef.detectChanges();
  }

  public ngOnDestroy(): void {
    this.subscriptions?.forEach((subscription) => subscription?.unsubscribe());
  }

  private prepareFilterPredicate(): void {
    this.dataSource.filterPredicate = (data: RowData, filter: string): boolean =>
      !filter ||
      this.columns().some(
        (field: TDatatableColumn<RowData, idType>) =>
          (
            (field.dataViewStrategy !== undefined
              ? String(field.dataViewStrategy(data))
              : String(data[(field as IDatatableColumn<RowData, idType>).id] as string)) ?? ''
          )
            .toLowerCase()
            .includes(filter) && field.isClientSideSearchable,
      );
  }

  private persistTableState(page: { pageIndex: number; pageSize: number }, sort: Sort): void {
    const { storageKey } = this.configurations();

    if (!storageKey) {
      return;
    }

    const state: ITableState = {
      pageIndex: page.pageIndex,
      pageSize: page.pageSize,
      sort: {
        active: sort.active,
        direction: sort.direction,
      },
    };

    this.tableStateService.setState(storageKey, state);
  }

  private restoreTableStateFromStorage(): void {
    const { storageKey } = this.configurations();

    if (!storageKey) {
      return;
    }

    const state: ITableState | null = this.tableStateService.getState(storageKey);

    if (!state) {
      return;
    }

    this.paginator.next({ pageIndex: this.paginator.value.pageIndex, pageSize: state.pageSize });

    /* This advanced logic can be used in the future */

    // const { defaultSort } = this.configurations();
    // const { defaultSort: baseDefaultSort } = this.defaultConfigurations;
    // const isStoredDefaultSort: boolean =
    //   state.sort.active === baseDefaultSort.active && state.sort.direction === baseDefaultSort.direction;
    // const shouldUseConfiguredDefaultSort: boolean =
    //   isStoredDefaultSort &&
    //   (defaultSort.active !== baseDefaultSort.active || defaultSort.direction !== baseDefaultSort.direction);
    // const sort: Sort = shouldUseConfiguredDefaultSort ? defaultSort : state.sort;

    // this.paginator.next({ pageIndex: state.pageIndex, pageSize: state.pageSize });
    // this.sort.next({ active: sort.active, direction: sort.direction });

    // if (shouldUseConfiguredDefaultSort) {
    //   this.persistTableState({ pageIndex: state.pageIndex, pageSize: state.pageSize }, sort);
    // }
  }
}
